import { usePlatform } from '../context/PlatformContext';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPaymentRequest, getContacts, updateContact, deleteContact, searchUsers } from '../utils/api';
import { QRCodeSVG as QRCode } from 'qrcode.react';

export default function RequestPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Request Payment';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const platform = usePlatform();
  const navigate = useNavigate();
  const qrRef    = useRef(null);

  const [amount, setAmount]             = useState('');
  const [description, setDescription]   = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [result, setResult]             = useState(null);
  const [copied, setCopied]             = useState(false);
  const [lineItems, setLineItems]       = useState([{ description: '', price: '' }]);

  // Contact state
  const [contacts, setContacts]               = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showNewContact, setShowNewContact]   = useState(false);
  const [contactDrop, setContactDrop]         = useState(false);
  const [contactQuery, setContactQuery]       = useState('');
  const [newFirst, setNewFirst]               = useState('');
  const [newLast, setNewLast]                 = useState('');
  const [newEmail, setNewEmail]               = useState('');
  const [newPhone, setNewPhone]               = useState('');

  // Thunderbolt user suggestions as you type
  const [userSuggestions, setUserSuggestions]   = useState([]);
  const [activeField, setActiveField]           = useState(null); // which field is searching
  const [tboltMatched, setTboltMatched]         = useState(false);

  // Edit state
  const [editingId, setEditingId]   = useState(null);
  const [editFirst, setEditFirst]   = useState('');
  const [editLast, setEditLast]     = useState('');
  const [editEmail, setEditEmail]   = useState('');
  const [editPhone, setEditPhone]   = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Suggestions
  const [descSuggestions, setDescSuggestions]         = useState([]);
  const [merchantSuggestions, setMerchantSuggestions] = useState([]);
  const [showDescDrop, setShowDescDrop]               = useState(false);
  const [showMerchantDrop, setShowMerchantDrop]       = useState(false);

  useEffect(() => {
    setDescSuggestions(JSON.parse(localStorage.getItem('tb_descriptions') || '[]'));
    setMerchantSuggestions(JSON.parse(localStorage.getItem('tb_merchants') || '[]'));
    loadContacts();
  }, []);

  // Search Thunderbolt users as new contact fields are typed
  useEffect(() => {
    if (tboltMatched) { setUserSuggestions([]); return; }
    const q = [newFirst, newLast, newEmail, newPhone].filter(Boolean).join(' ').trim();
    if (!q || q.length < 2) { setUserSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await searchUsers(q);
        setUserSuggestions(res.data || []);
      } catch { setUserSuggestions([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [newFirst, newLast, newEmail, newPhone, tboltMatched]);

  function addLineItem() {
    setLineItems(prev => [...prev, { description: '', price: '' }]);
  }

  function removeLineItem(i) {
    setLineItems(prev => prev.filter((_, idx) => idx !== i));
  }

  function updateLineItem(i, field, value) {
    setLineItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: value } : item));
    // Auto-calc total
    const updated = lineItems.map((item, idx) => idx === i ? { ...item, [field]: value } : item);
    const total = updated.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
    if (total > 0) setAmount(total.toFixed(2));
  }

  async function loadContacts() {
    try {
      const r = await getContacts();
      setContacts(r.data || []);
    } catch (e) {}
  }

  function saveToHistory(key, value) {
    if (!value?.trim()) return;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    localStorage.setItem(key, JSON.stringify([value, ...existing.filter(v => v !== value)]));
  }

  const amountCents = Math.round(parseFloat(amount || 0) * 100);
  const feeCents    = Math.round(amountCents * (platform.feeBps / 10000));

  const filteredContacts = contacts.filter(c =>
    (c.name || '').toLowerCase().includes(contactQuery.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(contactQuery.toLowerCase()) ||
    (c.phone || '').replace(/[^\d+]/g, '').includes(contactQuery.replace(/[^\d+]/g, ''))
  );

  function selectContact(c) {
    setSelectedContact(c);
    setContactQuery(c.name);
    setShowNewContact(false);
    setContactDrop(false);
    setEditingId(null);
  }

  function clearContact() {
    setSelectedContact(null);
    setContactQuery('');
    setShowNewContact(false);
    setNewFirst(''); setNewLast(''); setNewEmail(''); setNewPhone('');
    setUserSuggestions([]); setTboltMatched(false);
    setEditingId(null);
  }

  function fillFromTboltUser(user) {
    const parts = (user.name || '').split(' ');
    setNewFirst(parts[0] || '');
    setNewLast(parts.slice(1).join(' ') || '');
    setNewEmail(user.email || '');
    setNewPhone(user.phone || '');
    setUserSuggestions([]);
    setTboltMatched(true);
  }

  function startEdit(e, c) {
    e.stopPropagation();
    setEditingId(c.id);
    setEditFirst(c.firstName || c.name?.split(' ')[0] || '');
    setEditLast(c.lastName  || c.name?.split(' ').slice(1).join(' ') || '');
    setEditEmail(c.email || '');
    setEditPhone(c.phone || '');
  }

  async function saveEdit(e, c) {
    e.stopPropagation();
    if (!editFirst.trim()) return;
    setEditSaving(true);
    try {
      const res = await updateContact(c.id, { firstName: editFirst, lastName: editLast, email: editEmail, phone: editPhone });
      setContacts(prev => prev.map(x => x.id === c.id ? { ...x, ...res.data, name: res.data.name } : x));
      if (selectedContact?.id === c.id) setSelectedContact({ ...selectedContact, ...res.data });
      setEditingId(null);
    } catch (err) { console.error(err); }
    finally { setEditSaving(false); }
  }

  async function handleDelete(e, c) {
    e.stopPropagation();
    if (!window.confirm(`Delete ${c.name}?`)) return;
    setDeletingId(c.id);
    try {
      await deleteContact(c.id);
      setContacts(prev => prev.filter(x => x.id !== c.id));
      if (selectedContact?.id === c.id) clearContact();
    } catch (err) { console.error(err); }
    finally { setDeletingId(null); }
  }

  async function handleCreate() {
    if (amountCents < platform.minDepositCents) {
      setError(platform.minDepositCents > 0 ? `Minimum is $${(platform.minDepositCents/100).toFixed(2)}` : 'Please enter an amount');
      return;
    }
    setError(''); setLoading(true);
    try {
      const contactFirstName = showNewContact ? newFirst : selectedContact?.firstName || selectedContact?.name?.split(' ')[0] || undefined;
      const contactLastName  = showNewContact ? newLast  : selectedContact?.lastName  || selectedContact?.name?.split(' ').slice(1).join(' ') || undefined;
      const contactEmail     = showNewContact ? newEmail.trim().toLowerCase() : selectedContact?.email || undefined;
      const contactPhone     = showNewContact ? newPhone : selectedContact?.phone || undefined;

      const validLineItems = lineItems.filter(l => l.description.trim() && parseFloat(l.price) > 0);
      const payload = {
        amountCents, description, merchantName, lineItems: validLineItems,
        contactId:        selectedContact?.id || undefined,
        contactFirstName: contactFirstName || undefined,
        contactLastName:  contactLastName  || undefined,
        contactEmail:     contactEmail     || undefined,
        contactPhone:     contactPhone     || undefined,
      };
      const res = await createPaymentRequest(payload);
      saveToHistory('tb_descriptions', description);
      saveToHistory('tb_merchants', merchantName);
      setDescSuggestions(JSON.parse(localStorage.getItem('tb_descriptions') || '[]'));
      setMerchantSuggestions(JSON.parse(localStorage.getItem('tb_merchants') || '[]'));
      loadContacts();
      if (selectedContact) localStorage.setItem('tb_last_contact', JSON.stringify(selectedContact));
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create request');
    } finally { setLoading(false); }
  }

  function copyLink() {
    navigator.clipboard.writeText(result.shortUrl || result.paymentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function shareLink() {
    const url  = result.shortUrl || result.paymentLink;
    const text = `Pay $${result.amountUsd}${result.description ? ' for ' + result.description : ''}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Thunderbolt Payment', text, url }); } catch (e) {}
    } else { copyLink(); }
  }

  function downloadQR() {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 400;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#fff'; ctx.fillRect(0,0,400,400);
      ctx.drawImage(img,0,0,400,400);
      const a = document.createElement('a');
      a.download = `thunderbolt-$${result.amountUsd}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(svg))));
  }

  const contactDisplay      = selectedContact?.name || (showNewContact && newFirst ? [newFirst, newLast].filter(Boolean).join(' ') : null);
  const contactEmailDisplay = selectedContact?.email || (showNewContact ? newEmail : null);

  if (result) return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => setResult(null)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <span style={s.headerTitle}>Payment Request</span>
        <div style={{width:36}}/>
      </div>
      <div style={s.body}>
        <div style={s.qrCard}>
          {result.invoiceNumber && <div style={{fontSize:12, color:'#AEAEB2', marginBottom:4}}>Invoice #{result.invoiceNumber}</div>}
          <div style={s.qrAmount}>${result.amountUsd}</div>
          {result.description && <div style={s.qrDesc}>{result.description}</div>}
          {result.lineItems && result.lineItems.length > 0 && (
            <div style={{background:'#F5F5F7', borderRadius:10, padding:'10px 14px', marginBottom:12, textAlign:'left'}}>
              {result.lineItems.map((item, i) => (
                <div key={i} style={{display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0'}}>
                  <span style={{color:'#3D3D3D'}}>{item.description}</span>
                  <span style={{fontWeight:600}}>${parseFloat(item.price).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          {contactDisplay && (
            <div style={{fontSize:13, color:'#6E6E73', marginBottom:12}}>
              To: <strong>{contactDisplay}</strong>
              {contactEmailDisplay ? ` · ${contactEmailDisplay}` : ''}
            </div>
          )}
          <div style={s.qrWrap} ref={qrRef}>
            <QRCode value={result.shortUrl || result.paymentLink} size={180} level="H" includeMargin={true}/>
          </div>
          <div style={s.qrHint}>Scan with phone camera to pay</div>
          <button style={s.qrDownloadBtn} onClick={downloadQR}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight:6}}>
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download QR
          </button>
        </div>
        <div style={s.linkCard}>
          <div style={s.linkText}>{result.shortUrl || result.paymentLink}</div>
          <button style={s.copyBtn} onClick={copyLink}>{copied ? '✓' : 'Copy'}</button>
        </div>
        <div style={s.shareRow}>
          <button style={s.shareRowBtn} onClick={shareLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight:8}}>
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            {navigator.share ? 'Share link' : 'Copy link'}
          </button>
          <button style={s.shareRowBtnOutline} onClick={copyLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight:8}}>
              <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
            </svg>
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
        <div style={s.infoCard}>
          {[
            { label:'Amount',        value:`$${result.amountUsd}` },
            { label:'Merchant',      value:result.merchantName },
            { label:'Saved vs card', value:`~$${((amountCents*0.024)/100).toFixed(2)}`, green:true }
          ].map(row => (
            <div key={row.label} style={s.infoRow}>
              <span style={s.infoLabel}>{row.label}</span>
              <span style={{...s.infoVal, ...(row.green ? {color:'#00C48C'} : {})}}>{row.value}</span>
            </div>
          ))}
        </div>
        <button style={s.btn} onClick={() => { setResult(null); setAmount(''); setDescription(''); clearContact(); }}>New request</button>
        <button style={s.btnOutline} onClick={() => navigate('/dashboard')}>Dashboard</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate('/dashboard')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <span style={s.headerTitle}>Request Payment</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>
        <div style={s.amountCard}>
          <div style={s.fieldLabel}>Amount</div>
          <div style={s.amountRow}>
            <span style={s.dollar}>$</span>
            <input style={s.amountInput} type="number" placeholder="0.00" min="1" step="0.01" value={amount}
              onChange={e => { setAmount(e.target.value); setError(''); }}/>
          </div>
        </div>

        <div style={s.quickRow}>
          {[25,50,100,200].map(v => (
            <button key={v} style={{...s.quickBtn, ...(Number(amount)===v ? s.quickBtnActive : {})}}
              onClick={() => setAmount(v.toString())}>${v}</button>
          ))}
        </div>

        <div style={s.card}>
          {/* Description */}
          <div style={{...s.field, position:'relative'}}>
            <label style={s.fieldLabel}>Description (optional)</label>
            <input style={s.input} type="text" placeholder="e.g. Services Rendered" value={description}
              onChange={e => { setDescription(e.target.value); setShowDescDrop(true); }}
              onFocus={() => setShowDescDrop(true)}
              onBlur={() => setTimeout(() => setShowDescDrop(false), 150)}/>
            {showDescDrop && descSuggestions.filter(x => x.toLowerCase().includes(description.toLowerCase())).length > 0 && (
              <div style={s.suggDrop}>
                {descSuggestions.filter(x => x.toLowerCase().includes(description.toLowerCase())).slice(0,5).map((x,i) => (
                  <div key={i} style={s.suggItem} onMouseDown={() => { setDescription(x); setShowDescDrop(false); }}>{x}</div>
                ))}
              </div>
            )}
          </div>

          {/* Business name */}
          <div style={{...s.field, position:'relative'}}>
            <label style={s.fieldLabel}>Business name (optional)</label>
            <input style={s.input} type="text" placeholder="e.g. Acme Company" value={merchantName}
              onChange={e => { setMerchantName(e.target.value); setShowMerchantDrop(true); }}
              onFocus={() => setShowMerchantDrop(true)}
              onBlur={() => setTimeout(() => setShowMerchantDrop(false), 150)}/>
            {showMerchantDrop && merchantSuggestions.filter(x => x.toLowerCase().includes(merchantName.toLowerCase())).length > 0 && (
              <div style={s.suggDrop}>
                {merchantSuggestions.filter(x => x.toLowerCase().includes(merchantName.toLowerCase())).slice(0,5).map((x,i) => (
                  <div key={i} style={s.suggItem} onMouseDown={() => { setMerchantName(x); setShowMerchantDrop(false); }}>{x}</div>
                ))}
              </div>
            )}
          </div>

          {/* Contact picker */}
          <div style={{...s.field, marginBottom: showNewContact ? 12 : 0, position:'relative'}}>
            <label style={s.fieldLabel}>
              Send to (optional)
              {(selectedContact || showNewContact) && (
                <button onClick={clearContact} style={{marginLeft:8, background:'none', border:'none', color:'#AEAEB2', fontSize:11, cursor:'pointer', padding:0}}>clear</button>
              )}
            </label>
            <button style={{
              ...s.input, display:'flex', alignItems:'center', justifyContent:'space-between',
              cursor:'pointer', textAlign:'left',
              background: selectedContact ? '#EBF4FF' : '#FAFAFA',
              borderColor: selectedContact ? '#007AFF' : '#F0F0F0',
              color: selectedContact ? '#007AFF' : '#AEAEB2',
              fontWeight: selectedContact ? 500 : 400,
            }} onClick={() => { setContactDrop(o => !o); setEditingId(null); }}>
              <span>{selectedContact ? selectedContact.name : showNewContact ? 'New contact' : 'Select or add a contact...'}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                style={{transform: contactDrop ? 'rotate(180deg)' : 'none', transition:'transform 0.2s', flexShrink:0}}>
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>

            {contactDrop && (
              <>
                <div style={{position:'fixed', inset:0, zIndex:49}} onClick={() => { setContactDrop(false); setEditingId(null); }}/>
                <div style={s.suggDrop}>
                  {filteredContacts.length > 0 && (
                    <div style={{padding:'8px 14px 4px', fontSize:11, fontWeight:600, color:'#AEAEB2', textTransform:'uppercase', letterSpacing:0.5}}>
                      Saved contacts
                    </div>
                  )}
                  {filteredContacts.map(c => (
                    <div key={c.id} style={{borderBottom:'1px solid #F5F5F7'}}>
                      {editingId === c.id ? (
                        <div style={{padding:'12px 14px'}} onClick={e => e.stopPropagation()}>
                          <div style={{display:'flex', gap:8, marginBottom:8}}>
                            <input style={s.editInput} placeholder="First" value={editFirst} onChange={e => setEditFirst(e.target.value)}/>
                            <input style={s.editInput} placeholder="Last"  value={editLast}  onChange={e => setEditLast(e.target.value)}/>
                          </div>
                          <input style={{...s.editInput, width:'100%', marginBottom:8, boxSizing:'border-box'}} placeholder="Email" value={editEmail} onChange={e => setEditEmail(e.target.value)}/>
                          <input style={{...s.editInput, width:'100%', marginBottom:10, boxSizing:'border-box'}} placeholder="Phone" value={editPhone} onChange={e => setEditPhone(e.target.value)}/>
                          <div style={{display:'flex', gap:8}}>
                            <button style={s.editSaveBtn} onMouseDown={e => saveEdit(e, c)} disabled={editSaving}>
                              {editSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button style={s.editCancelBtn} onMouseDown={e => { e.stopPropagation(); setEditingId(null); }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{display:'flex', alignItems:'center', padding:'10px 14px', gap:8}}>
                          <div style={{flex:1, cursor:'pointer'}} onMouseDown={() => selectContact(c)}>
                            <div style={{fontWeight:500, color:'#1D1D1F', fontSize:14}}>{c.name}</div>
                            <div style={{fontSize:12, color:'#AEAEB2'}}>
                              {[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact info'}
                            </div>
                          </div>
                          <button style={s.iconBtn} onMouseDown={e => startEdit(e, c)} title="Edit">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button style={{...s.iconBtn, opacity: deletingId === c.id ? 0.4 : 1}} onMouseDown={e => handleDelete(e, c)} disabled={deletingId === c.id} title="Delete">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF3B30" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  <div style={{...s.suggItem, color:'#007AFF', fontWeight:600, borderBottom:'none', display:'flex', alignItems:'center', gap:8}}
                    onMouseDown={() => { setShowNewContact(true); setSelectedContact(null); setContactDrop(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    New contact
                  </div>
                </div>
              </>
            )}
          </div>

          {/* New contact fields */}
          {showNewContact && (
            <div style={{borderTop:'1px solid #F5F5F7', paddingTop:16, position:'relative'}}>

              {/* Thunderbolt user suggestions dropdown */}
              {userSuggestions.length > 0 && (
                <div style={{...s.suggDrop, position:'absolute', top:16, left:0, right:0, zIndex:60}}>
                  <div style={{padding:'8px 14px 4px', fontSize:11, fontWeight:600, color:'#007AFF', textTransform:'uppercase', letterSpacing:0.5}}>
                    ⚡ Thunderbolt users
                  </div>
                  {userSuggestions.map(u => (
                    <div key={u.id} style={{...s.suggItem, display:'flex', alignItems:'center', gap:10}}
                      onMouseDown={() => fillFromTboltUser(u)}>
                      <div style={s.miniAvatar}>{(u.name||'?').slice(0,2).toUpperCase()}</div>
                      <div>
                        <div style={{fontWeight:500, fontSize:14, color:'#1D1D1F'}}>{u.name}</div>
                      <div style={{fontSize:12, color:'#AEAEB2'}}>
                          {[u.email, u.phone].filter(Boolean).join(' · ') || 'Thunderbolt user'}
                        </div>
                      </div>
                      <div style={{marginLeft:'auto', fontSize:11, color:'#007AFF', fontWeight:600}}>Tap to fill</div>
                    </div>
                  ))}
                </div>
              )}

              {tboltMatched && (
                <div style={{display:'flex', lignItems:'center', gap:6, marginBottom:12, fontSize:12, color:'#00C48C', fontWeight:500}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  Filled from Thunderbolt
                  <button onClick={() => { setTboltMatched(false); setNewFirst(''); setNewLast(''); setNewEmail(''); setNewPhone(''); }}
                    style={{marginLeft:4, background:'none', border:'none', color:'#AEAEB2', fontSize:11, cursor:'pointer', padding:0}}>clear</button>
                </div>
              )}

              <div style={{display:'flex', gap:10, marginBottom:10}}>
                <div style={{flex:1}}>
                  <label style={s.fieldLabel}>First name</label>
                  <input style={s.input} type="text" placeholder="First" value={newFirst}
                    onChange={e => { setNewFirst(e.target.value); setTboltMatched(false); }}/>
                </div>
                <div style={{flex:1}}>
                  <label style={s.fieldLabel}>Last name</label>
                  <input style={s.input} type="text" placeholder="Last" value={newLast}
                    onChange={e => { setNewLast(e.target.value); setTboltMatched(false); }}/>
                </div>
              </div>
              <div style={{marginBottom:10}}>
                <label style={s.fieldLabel}>Email</label>
                <input style={s.input} type="email" placeholder="email@example.com" value={newEmail}
                  onChange={e => { setNewEmail(e.target.value); setTboltMatched(false); }}/>
              </div>
              <div>
                <label style={s.fieldLabel}>Phone</label>
                <input style={s.input} type="tel" placeholder="+1 (555) 000-0000" value={newPhone}
                  onChange={e => { setNewPhone(e.target.value); setTboltMatched(false); }}/>
              </div>
            </div>
          )}
        </div>

        {/* Line Items */}
        <div style={s.card}>
          <div style={s.fieldLabel}>Line items (optional)</div>
          {lineItems.map((item, i) => (
            <div key={i} style={{display:'flex', gap:8, marginBottom:8, alignItems:'center'}}>
              <input
                style={{...s.input, flex:2}}
                type="text"
                placeholder="Description"
                value={item.description}
                onChange={e => updateLineItem(i, 'description', e.target.value)}
              />
              <div style={{display:'flex', alignItems:'center', background:'#FAFAFA', border:'1.5px solid #F0F0F0', borderRadius:12, padding:'0 12px', flex:1}}>
                <span style={{color:'#6E6E73', fontSize:14, fontWeight:600}}>$</span>
                <input
                  style={{...s.input, border:'none', background:'transparent', padding:'13px 6px', flex:1}}
                  type="number"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={item.price}
                  onChange={e => updateLineItem(i, 'price', e.target.value)}
                />
              </div>
              {lineItems.length > 1 && (
                <button onClick={() => removeLineItem(i)} style={{background:'none', border:'none', color:'#FF3B30', fontSize:18, cursor:'pointer', padding:'0 4px'}}>✕</button>
              )}
            </div>
          ))}
          <button onClick={addLineItem} style={{background:'none', border:'1.5px dashed #D1D1D6', borderRadius:10, padding:'8px 14px', fontSize:13, color:'#007AFF', fontWeight:500, cursor:'pointer', width:'100%', marginTop:4}}>
            + Add line item
          </button>
        </div>

        {amountCents > 0 && platform.loaded && (
          <div style={s.savingsCard}>
            <div style={s.savingsTitle}>Fee comparison on ${(amountCents/100).toFixed(2)}</div>
            <div style={s.savingsRow}>
              <span style={s.savingsLabel}>Credit card (2.9%)</span>
              <span style={s.savingsHigh}>${((amountCents*0.029)/100).toFixed(2)}</span>
            </div>
            <div style={s.savingsRow}>
              <span style={s.savingsLabel}>{`Thunderbolt (${platform.feePct}%)`}</span>
              <span style={s.savingsLow}>${(feeCents/100).toFixed(2)}</span>
            </div>
            <div style={s.savingsDivider}/>
            <div style={s.savingsRow}>
              <span style={{...s.savingsLabel, fontWeight:600, color:'#1D1D1F'}}>You save</span>
              <span style={s.savingsGreen}>${((amountCents*0.029 - feeCents)/100).toFixed(2)}</span>
            </div>
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <button style={s.btn} onClick={handleCreate} disabled={loading}>
          {loading ? 'Creating...' : 'Create payment request'}
        </button>
      </div>
    </div>
  );
}

const s = {
  page:           { minHeight:'100vh', background:'#F5F5F7', paddingBottom:88 },
  header:         { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px' },
  back:           { width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)' },
  headerTitle:    { fontSize:17, fontWeight:600, color:'#1D1D1F' },
  body:           { padding:'0 16px' },
  amountCard:     { background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  fieldLabel:     { fontSize:12, fontWeight:500, color:'#6E6E73', marginBottom:8, display:'block' },
  amountRow:      { display:'flex', alignItems:'center', gap:4 },
  dollar:         { fontSize:36, fontWeight:600 },
  amountInput:    { fontSize:36, fontWeight:600, border:'none', background:'transparent', width:'100%', outline:'none' },
  quickRow:       { display:'flex', gap:8, marginBottom:12 },
  quickBtn:       { flex:1, padding:'10px 0', background:'#fff', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:14, fontWeight:500, color:'#1D1D1F' },
  quickBtnActive: { borderColor:'#007AFF', color:'#007AFF', background:'#EBF4FF' },
  card:           { background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  field:          { marginBottom:16 },
  input:          { width:'100%', padding:'13px 14px', border:'1.5px solid #F0F0F0', borderRadius:12, fontSize:14, background:'#FAFAFA', color:'#1D1D1F', outline:'none', boxSizing:'border-box' },
  suggDrop:       { position:'absolute', top:'100%', left:0, right:0, background:'#fff', borderRadius:12, zIndex:50, boxShadow:'0 8px 30px rgba(0,0,0,0.12)', overflow:'hidden', marginTop:4, maxHeight:280, overflowY:'auto' },
  suggItem:       { padding:'12px 14px', fontSize:14, color:'#1D1D1F', cursor:'pointer', borderBottom:'1px solid #F5F5F7' },
  iconBtn:        { background:'none', border:'none', padding:'4px 6px', cursor:'pointer', borderRadius:6, display:'flex', alignItems:'center', flexShrink:0 },
  editInput:      { padding:'8px 10px', border:'1.5px solid #E5E5EA', borderRadius:8, fontSize:13, background:'#FAFAFA', color:'#1D1D1F', outline:'none', flex:1 },
  editSaveBtn:    { flex:1, padding:'8px 0', background:'#007AFF', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' },
  editCancelBtn:  { flex:1, padding:'8px 0', background:'#F5F5F7', color:'#6E6E73', border:'none', borderRadius:8, fontSize:13, fontWeight:500, cursor:'pointer' },
  miniAvatar:     { width:32, height:32, borderRadius:'50%', background:'#EBF4FF', color:'#007AFF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, flexShrink:0 },
  savingsCard:    { background:'#fff', borderRadius:16, padding:16, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  savingsTitle:   { fontSize:12, fontWeight:500, color:'#6E6E73', marginBottom:10 },
  savingsRow:     { display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0' },
  savingsLabel:   { color:'#6E6E73' },
  savingsHigh:    { color:'#FF3B30' },
  savingsLow:     { color:'#6E6E73' },
  savingsDivider: { height:1, background:'#F5F5F7', margin:'8px 0' },
  savingsGreen:   { color:'#00C48C', fontWeight:600 },
  error:          { background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12 },
  btn:            { width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3, marginBottom:8 },
  btnOutline:     { width:'100%', padding:16, background:'#fff', color:'#007AFF', border:'1.5px solid #007AFF', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3 },
  qrCard:         { background:'#fff', borderRadius:20, padding:24, textAlign:'center', marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  qrAmount:       { fontSize:36, fontWeight:700, letterSpacing:-1, marginBottom:4 },
  qrDesc:         { fontSize:14, color:'#6E6E73', marginBottom:8 },
  qrWrap:         { display:'flex', justifyContent:'center', marginBottom:12 },
  qrHint:         { fontSize:13, color:'#AEAEB2', marginBottom:12 },
  qrDownloadBtn:  { display:'inline-flex', alignItems:'center', padding:'8px 16px', background:'#F5F5F7', border:'none', borderRadius:10, fontSize:13, fontWeight:500, color:'#1D1D1F', cursor:'pointer' },
  linkCard:       { background:'#fff', borderRadius:14, padding:'12px 16px', display:'flex', alignItems:'center', gap:8, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  linkText:       { flex:1, fontSize:12, color:'#6E6E73', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  copyBtn:        { padding:'8px 14px', background:'#007AFF', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:500, whiteSpace:'nowrap' },
  shareRow:       { display:'flex', gap:8, marginBottom:12 },
  shareRowBtn:    { flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'14px 0', background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:15, fontWeight:600 },
  shareRowBtnOutline: { flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'14px 0', background:'#fff', color:'#007AFF', border:'1.5px solid #007AFF', borderRadius:14, fontSize:15, fontWeight:600 },
  infoCard:       { background:'#fff', borderRadius:16, padding:16, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  infoRow:        { display:'flex', justifyContent:'space-between', fontSize:13, padding:'5px 0', borderBottom:'1px solid #F5F5F7' },
  infoLabel:      { color:'#6E6E73' },
  infoVal:        { fontWeight:500 },
};
